import {
  buildSearchQueryString,
  collectFromSearchRoot,
  readSearchParams,
} from '../../../js/offerSearchParams.js';
import {
  INTENT,
  getSearchCatalogUrls,
  resolveFormAction,
} from '../../../js/offerSearchRules.js';
import {
  getUnifiedListing,
  isUnifiedListingContext,
} from '../../../js/unifiedListingContext.js';

const DEBOUNCE_MS = 250;
const DEFAULT_PER_PAGE = 12;

const getSearchRoot = () => {
  const listing = getUnifiedListing();
  if (!listing) return null;
  return document.querySelector('.js-offer-search')
    || listing.closest('main')?.querySelector('.js-offer-search')
    || null;
};

const getEndpoint = (listing) => (
  listing?.dataset.unifiedEndpoint
  || window.hskiSearch?.offersUnifiedUrl
  || ''
);

const readListingPage = (listing) => {
  const fromListing = Number(listing?.dataset.offersPage || 0);
  if (fromListing > 0) return fromListing;
  const fromUrl = Number(readSearchParams().paged || 1);
  return Math.max(1, fromUrl || 1);
};

const collectWireParams = (searchRoot, listing, { page } = {}) => {
  const params = collectFromSearchRoot(searchRoot);
  const decision = resolveFormAction(
    params.destinations,
    params.types,
    getSearchCatalogUrls(),
  );

  const sortSelect = listing?.querySelector('.js-filter-sort');
  const sort = sortSelect?.value || params.sort || 'price-asc';

  const board = params.board || [];
  const amenities = params.amenities || [];
  const hotelFiltersOn = Boolean(params.meals)
    || Boolean(params.allInclusive)
    || board.length > 0
    || amenities.length > 0;

  const paged = Math.max(1, Number(page ?? readListingPage(listing)) || 1);

  // Browse /oferty/: empty dest+types → package (SearchContract + SSR prepare_query_params).
  // Without this, resolveFormAction returns invalid and AJAX omits intent → 0 results.
  const isBrowseEmpty = decision.destinations.length === 0
    && decision.types.length === 0;
  const intent = (isBrowseEmpty && (decision.intent === INTENT.INVALID || !decision.intent))
    ? INTENT.PACKAGE
    : decision.intent;
  const packageOnly = intent === INTENT.PACKAGE
    || Boolean(decision.flags?.packageOnly)
    || hotelFiltersOn;
  const trainingOnly = intent === INTENT.TRAINING
    || Boolean(decision.flags?.trainingOnly);

  // Always prefer decision-normalized types/dest (never fall back to stale
  // raw form types like wyjazd-hotel after switching to Poland).
  // Hotel chips (meals/board/amenities) are independent of Kiedy/Osoby.
  return {
    ...params,
    intent,
    types: decision.types,
    destinations: decision.destinations,
    packageOnly,
    trainingOnly,
    dateFrom: decision.showDateGuests ? params.dateFrom : '',
    dateTo: decision.showDateGuests ? params.dateTo : '',
    adults: decision.showDateGuests ? params.adults : '',
    children: decision.showDateGuests ? params.children : '',
    childDobs: decision.showDateGuests ? params.childDobs : [],
    meals: Boolean(params.meals),
    allInclusive: Boolean(params.allInclusive),
    transport: Boolean(params.transport),
    training: Boolean(params.training),
    noTraining: Boolean(params.noTraining),
    board,
    amenities,
    sort,
    paged,
    perPage: DEFAULT_PER_PAGE,
  };
};

const syncUrl = (wireParams) => {
  const query = buildSearchQueryString(wireParams);
  const next = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  if (`${window.location.pathname}${window.location.search}` !== next) {
    window.history.replaceState({}, '', next);
  }
};

const LOADING_MIN_MS = 380;
const LOADING_MIN_REDUCED_MS = 120;
const MOBILE_LISTING_MQ = '(max-width: 1024px)';

const getStickyOffsetPx = () => (
  Number.parseFloat(
    getComputedStyle(document.body).getPropertyValue('--sticky-offset').trim(),
  ) || 72
);

const isMobileListingViewport = () => window.matchMedia(MOBILE_LISTING_MQ).matches;

/**
 * Mobile: kotwica od widoczności filtrów (pasek sezon + Filtry).
 * Desktop: wyniki / cały listing (jak dotychczas).
 */
const getListingScrollAnchor = (listing) => {
  if (isMobileListingViewport()) {
    const searchRoot = getSearchRoot();
    return searchRoot?.querySelector('.offer-search__toolbar')
      || searchRoot
      || listing.querySelector('.offers-list-zagraniczne__toolbar')
      || listing;
  }
  return listing.querySelector('.js-offers-results') || listing;
};

const scrollListingIntoView = (listing, { force = false } = {}) => {
  const anchor = getListingScrollAnchor(listing);
  if (!anchor) return;

  const sticky = getStickyOffsetPx();
  const top = anchor.getBoundingClientRect().top;
  if (!force && top >= sticky + 8 && top <= window.innerHeight * 0.55) return;

  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const y = window.scrollY + top - sticky - 8;
  window.scrollTo({
    top: Math.max(0, y),
    behavior: reduced ? 'auto' : 'smooth',
  });
};

/** Blok kart / empty / paginacji — bez toolbara i bez wyszukiwarki. */
const ensureOffersResultsHost = (listing) => {
  let host = listing.querySelector('.js-offers-results');
  if (host) return host;

  const container = listing.querySelector('.container') || listing;
  const toolbar = container.querySelector('.offers-list-zagraniczne__toolbar');
  host = document.createElement('div');
  host.className = 'offers-list-zagraniczne__results js-offers-results';

  const moveNodes = [];
  let node = toolbar ? toolbar.nextSibling : container.firstChild;
  while (node) {
    const next = node.nextSibling;
    if (node.nodeType === 1 && !node.classList.contains('js-offers-list-loading')) {
      moveNodes.push(node);
    }
    node = next;
  }
  moveNodes.forEach((el) => host.appendChild(el));

  if (toolbar) {
    toolbar.after(host);
  } else {
    container.appendChild(host);
  }
  return host;
};

const ensureListingLoadingEl = (listing) => {
  const host = ensureOffersResultsHost(listing);
  let el = host.querySelector('.js-offers-list-loading');
  if (el) return el;
  el = document.createElement('div');
  el.className = 'offers-list-zagraniczne__loading js-offers-list-loading';
  el.setAttribute('aria-hidden', 'true');
  el.innerHTML = `
    <div class="offers-list-zagraniczne__loading-panel" role="status" aria-live="polite">
      <span class="offers-list-zagraniczne__loading-spinner" aria-hidden="true"></span>
      <span class="offers-list-zagraniczne__loading-label">Ładowanie ofert…</span>
    </div>
  `;
  host.appendChild(el);
  return el;
};

const setListingLoading = (listing, loading) => {
  const overlay = ensureListingLoadingEl(listing);
  listing.classList.toggle('is-loading', loading);
  listing.setAttribute('aria-busy', loading ? 'true' : 'false');
  overlay.setAttribute('aria-hidden', loading ? 'false' : 'true');
  if (loading) {
    listing.dataset.loadingStartedAt = String(Date.now());
    // Mobile: od filtrów; desktop: góra wyników z loaderem.
    scrollListingIntoView(listing);
  }
};

const finishListingLoading = async (listing, seq) => {
  if (seq !== reloadSeq) return;
  const started = Number(listing.dataset.loadingStartedAt || 0);
  const minMs = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? LOADING_MIN_REDUCED_MS
    : LOADING_MIN_MS;
  const wait = Math.max(0, minMs - (Date.now() - started));
  if (wait > 0) {
    await new Promise((resolve) => window.setTimeout(resolve, wait));
  }
  if (seq !== reloadSeq) return;
  setListingLoading(listing, false);
  delete listing.dataset.loadingStartedAt;
};

const applyUnifiedResponse = (listing, data) => {
  const cardsEl = listing.querySelector('.js-offers-cards');
  const countEl = listing.querySelector('.js-offers-count');
  const emptyEl = listing.querySelector('.js-offers-empty');
  const paginationRoot = listing.querySelector('.js-offers-pagination-root');
  const total = Number(data.total) || 0;
  const isEmpty = Boolean(data.is_empty) || total === 0;
  const paged = Math.max(1, Number(data.paged || data.pagination?.current_page || 1) || 1);
  const lastPage = Math.max(1, Number(data.last_page || data.pagination?.last_page || 1) || 1);

  if (cardsEl) {
    cardsEl.innerHTML = data.html || '';
    cardsEl.classList.toggle('is-empty', isEmpty);
  }

  if (countEl) {
    countEl.textContent = data.count_label || `${total} z ${total} ofert`;
  }

  if (paginationRoot) {
    paginationRoot.innerHTML = data.pagination_html || '';
  }

  listing.dataset.offersTotal = String(total);
  listing.dataset.offersPage = String(paged);
  listing.dataset.offersLastPage = String(lastPage);
  listing.classList.toggle('is-empty', isEmpty);
  listing.classList.toggle('has-results', !isEmpty);

  if (emptyEl) {
    emptyEl.classList.toggle('d-none', !isEmpty);
  }

  // Lazy: avoid static OfferZagraniczna → Swiper/PhotoSwipe on OfferSearch graph.
  import('../../parts/OfferZagraniczna/OfferZagraniczna.js')
    .then((m) => m.initOfferGalleries())
    .catch(() => {});
  import('../../../js/imageSkeleton.js')
    .then((m) => m.bindImageSkeletons(cardsEl || listing))
    .catch(() => {});
};

let reloadTimer = 0;
let reloadSeq = 0;
let reloadAbort = null;
const RELOAD_TIMEOUT_MS = 20000;

export const reloadUnifiedOffers = async ({ scroll = false, page } = {}) => {
  const listing = getUnifiedListing();
  const searchRoot = getSearchRoot();
  const endpoint = getEndpoint(listing);
  if (!listing || !searchRoot || !endpoint) return;

  const wireParams = collectWireParams(searchRoot, listing, { page });
  listing.dataset.offersPage = String(wireParams.paged);
  syncUrl(wireParams);

  const query = buildSearchQueryString(wireParams);
  const url = query ? `${endpoint}?${query}` : endpoint;
  const seq = ++reloadSeq;

  reloadAbort?.abort();
  const ac = new AbortController();
  reloadAbort = ac;
  const timeoutId = window.setTimeout(() => ac.abort(), RELOAD_TIMEOUT_MS);

  setListingLoading(listing, true);

  try {
    const response = await fetch(url, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: ac.signal,
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    if (seq !== reloadSeq) return;
    applyUnifiedResponse(listing, data);
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.warn('[HSKI] unified offers reload failed', error);
    }
  } finally {
    window.clearTimeout(timeoutId);
    await finishListingLoading(listing, seq);
  }

  if (scroll && seq === reloadSeq) {
    scrollListingIntoView(listing, { force: true });
  }
};

export const scheduleUnifiedOffersReload = (options = {}) => {
  if (!getUnifiedListing()) return;
  window.clearTimeout(reloadTimer);
  reloadTimer = window.setTimeout(() => {
    reloadUnifiedOffers(options);
  }, DEBOUNCE_MS);
};

const goToPage = (page) => {
  const listing = getUnifiedListing();
  if (!listing) return;
  const last = Math.max(1, Number(listing.dataset.offersLastPage || 1) || 1);
  const next = Math.min(last, Math.max(1, Number(page) || 1));
  scheduleUnifiedOffersReload({ scroll: true, page: next });
};

const initUnifiedListingControls = (listing) => {
  const sortSelect = listing.querySelector('.js-filter-sort');
  sortSelect?.addEventListener('change', () => {
    scheduleUnifiedOffersReload({ page: 1 });
  });

  listing.addEventListener('click', (event) => {
    const pageBtn = event.target.closest('.js-offers-page');
    if (pageBtn) {
      event.preventDefault();
      goToPage(pageBtn.dataset.page);
      return;
    }

    if (event.target.closest('.js-offers-page-prev')) {
      event.preventDefault();
      goToPage(readListingPage(listing) - 1);
      return;
    }

    if (event.target.closest('.js-offers-page-next')) {
      event.preventDefault();
      goToPage(readListingPage(listing) + 1);
    }
  });
};

const initOffersUnifiedListing = () => {
  const listing = getUnifiedListing();
  if (!listing) return;

  listing.dataset.unifiedBound = '1';
  listing.dataset.offersPage = String(readListingPage(listing));
  const pagination = listing.querySelector('.js-offers-pagination');
  if (pagination?.dataset.lastPage) {
    listing.dataset.offersLastPage = pagination.dataset.lastPage;
  }
  initUnifiedListingControls(listing);

  document.addEventListener('hski:search-change', (event) => {
    if (!isUnifiedListingContext()) return;
    const detail = event.detail || {};
    // Filtry / wyszukiwanie — wróć na stronę 1
    scheduleUnifiedOffersReload({ scroll: Boolean(detail.scroll), page: 1 });
  });
};

export { isUnifiedListingContext };
export default initOffersUnifiedListing;
